import { shallowEqual, useDispatch, useSelector } from "react-redux";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import { AppState } from "@Store/index";
import { getGalleryTabFor3DViews, getItemsByCategories } from "../../SectionFeatured.tsx/utils";
import { transformVideoPath } from "../../ReviewsSection/data/helper";
import { scrollToSection } from "../../ActionButtonSection/utils";

/**
 * The desktop hero gallery's actions, shared by both experiment arms: SectionFeaturedDesktop
 * (control) and OverviewGallery (variant) open the same gallery modal. The map / floor-plan
 * scrolls mirror ActionButtonSection.
 */
const usePropertyGalleryActions = () => {
  const dispatch = useDispatch();
  const { data, reviewData, roomTypesData } = useSelector(
    ({ propertyPageData }: AppState) => ({
      data: propertyPageData.data,
      reviewData: propertyPageData.reviewData?.result,
      roomTypesData: propertyPageData.roomTypes?.data || [],
    }),
    shallowEqual,
  );

  const openGallery = (tab: "all" | "videos" | "3D") => {
    const galleryTab = tab === "3D" ? getGalleryTabFor3DViews(data, roomTypesData) : tab;
    const residentMedia =
      reviewData?.flatMap(({ media, rating, user_details, anonymous }: any) =>
        (media || []).map(({ path, thumbnail_url }: any) => ({
          path: transformVideoPath(path),
          base_path: transformVideoPath(path),
          category: thumbnail_url ? "videos" : "images",
          platform: "gumlet",
          thumbnail_url: thumbnail_url ? transformVideoPath(thumbnail_url) : null,
          userName: user_details?.name,
          anonymous,
          rating,
          showCaption: true,
        })),
      ) || [];
    const sitePlan =
      data?.site_plan?.map(
        ({ building, number_of_floors, floor_details: [...floorDetails] }: any) => ({
          building,
          number_of_floors,
          floor_details: floorDetails?.sort(
            (a: any, b: any) => parseFloat(a?.floor) - parseFloat(b?.floor),
          ),
        }),
      ) || [];

    const additionalData: any = {};
    if (data?.site_plan?.length > 0) additionalData.site_plan = sitePlan;
    if (residentMedia.length > 0) additionalData.shot_by_residents = residentMedia;

    // floor_plan and site_plan go last so they are the last tabs, as today.
    const allItems: any = { ...getItemsByCategories(data), ...additionalData };
    const { floor_plan, site_plan, ...galleryItems } = allItems;
    if (floor_plan) galleryItems.floor_plan = floor_plan;
    if (site_plan) galleryItems.site_plan = site_plan;

    new PropertyPageDispatcher(dispatch).showGalleryModal({
      show: true,
      selectedTab: galleryTab || "all",
      data: galleryItems,
    });
  };

  const scrollToMap = () => scrollToSection("map");

  // SectionNavigation owns the floor-plan scroll (it corrects for lazy sections shifting layout).
  const scrollToFloorPlans = () => {
    const navigate = () =>
      window.dispatchEvent(
        new CustomEvent("navigate_to_section", { detail: { id: "floor-layout" } }),
      );
    if (document.getElementById("floor-layout")) navigate();
    else setTimeout(navigate, 300);
  };

  return { openGallery, scrollToMap, scrollToFloorPlans };
};

export default usePropertyGalleryActions;
